import { DateRange } from "react-day-picker";
import { format } from "date-fns";
import { Calendar as CalendarIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

export interface CalendarDateRangePickerProps {
  dateRange: { start: string; end: string } | undefined;
  setDateRange: (range: { start: string; end: string } | undefined) => void;
  className?: string;
  isDarkMode?: boolean;
}

export function CalendarDateRangePicker({
  dateRange,
  setDateRange,
  className,
  isDarkMode,
}: CalendarDateRangePickerProps) {
  const parsedDateRange: DateRange | undefined = dateRange
    ? {
        from: dateRange.start ? new Date(dateRange.start) : undefined,
        to: dateRange.end ? new Date(dateRange.end) : undefined,
      }
    : undefined;

  const handleSelect = (range: DateRange | undefined) => {
    const formattedRange = range
      ? {
          start: range.from ? format(range.from, "yyyy-MM-dd") : "",
          end: range.to ? format(range.to, "yyyy-MM-dd") : "",
        }
      : undefined;
    setDateRange(formattedRange);
  };

  return (
    <div className={cn("grid gap-2", className)}>
      <Popover>
        <PopoverTrigger asChild>
          <Button
            id="date"
            variant={"outline"}
            className={cn(
              "w-full justify-start text-left font-normal",
              !dateRange && "text-muted-foreground",
              isDarkMode ? "bg-gray-700 border-gray-600 text-gray-200" : ""
            )}
          >
            <CalendarIcon className="mr-2 h-4 w-4" />
            {dateRange?.start && dateRange?.end ? (
              <>
                {format(new Date(dateRange.start), "LLL dd, y")} -{" "}
                {format(new Date(dateRange.end), "LLL dd, y")}
              </>
            ) : dateRange?.start ? (
              format(new Date(dateRange.start), "LLL dd, y")
            ) : (
              <span>Pick a date range</span>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className={cn(
            "w-auto p-0",
            isDarkMode ? "bg-gray-800 border-gray-700" : ""
          )}
          align="end"
        >
          <Calendar
            initialFocus
            mode="range"
            defaultMonth={parsedDateRange?.from}
            selected={parsedDateRange}
            onSelect={handleSelect}
            numberOfMonths={2}
            disabled={{ after: new Date() }}
            className="p-3 pointer-events-auto"
          />
        </PopoverContent>
      </Popover>
    </div>
  );
}
